import React, { useState, useRef } from "react";
import { useTheme, Box, Pagination, Button, Chip, Typography } from "@mui/material";
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from "react-i18next";
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";

import { RecordTableWidgetData } from "./RecordTable";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import { ColumnModel, ColumnModelItem } from "../../../tables/columns/ColumnModel";
import numberColumn from "components/tables/columns/NumberColumn";
import dateTimeColumn from "components/tables/columns/DateTimeColumn";
import textColumn from "components/tables/columns/TextColumn";
import chipColumn from "components/tables/columns/ChipColumn";
import ISODateTime from "components/formatting/ISODateTime";
import ISONumber from "components/formatting/ISONumber";
import SanitizedHTML from "components/formatting/SanitizedHTML";
import StripedDataGrid from "components/uiElements/dataGrid/StripedDataGrid";

interface RecordTableContentProps {
  apiResponse: any;
  records: Array<any>;
  columnModel?: ColumnModel;
  currentState?: Record<string, any>;
  pageSize?: number;
  recordTableData?: RecordTableWidgetData;
  widgetQueryTitle?: string;
  onFetchMore?: () => void;
  onScrollToRef?: () => void;
}

const getColumnType = (type: string) => {
  switch (type) {
    case "text":
      return textColumn();
    case "integer":
      return numberColumn();
    case "timestamp":
      return dateTimeColumn();
    case "keyword":
    case "keywords":
    case "latitude_longitude":
      return chipColumn();
    default:
      return undefined;
  }
};

export function getValueLabels({
  apiValueLabels,
  field,
  lookupKey
}: {
  apiValueLabels: Record<string, { [key: string]: string }>, 
  field: string,
  lookupKey: string | string[]
}) {
  if (Array.isArray(lookupKey)) {
    const fieldValueLabels = lookupKey.map(item => apiValueLabels[field][item]);
    return fieldValueLabels;
  } else {
    return apiValueLabels[field][lookupKey];
  }
}

export default function RecordTableContent(props: RecordTableContentProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const records = props.records || [];
  const fieldLabels = props.apiResponse?.field_labels || {};
  const fieldTypes = props.apiResponse?.field_types || {}; // Types: ["text", "integer", "timestamp", "keyword", "keywords", "latitude_longitude"]

  const columnWidth = 150;

  const rows = records.map((record, index) => ({ id: index, ...record, }));
  const [pageNumber, setPageNumber] = useState<number>(1);
  const pageSize = props.currentState?.pageSize || props.pageSize;
  const pageCount = Math.ceil(rows.length / pageSize);
  const paginatedRows = rows.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  const cursor = props.apiResponse?.cursor || null;

  // Scroll to top on page change
  const tableRef = useRef<HTMLDivElement>(null);
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageNumber(value);
    if (props.onScrollToRef) {
      // If a scroll function is provided, use it (for scrolling within a dialog)
      props.onScrollToRef();
      return;
    }
    // DataGrid manages its own internal scroll area via ".MuiDataGrid-virtualScroller", so we can't scroll 
    // via outer container ref. This could be done cleanly via useGridApiRef (paid), but the free edition
    // doesn't expose a scroll API — querying internal scroller element directly is the recommended workaround.
    const dataGridScroller = tableRef.current?.querySelector<HTMLDivElement>(".MuiDataGrid-virtualScroller");
    dataGridScroller?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Fetch more records if at the end of the list
  const isAtEnd = pageNumber === pageCount && cursor !== null;
  const handleFetchMore = () => {
    if (props.onFetchMore) {
      props.onFetchMore();
    }
    setPageNumber(pageNumber + 1);
  };

  const initialModelColumns: ColumnModelItem[] = Object.keys(fieldLabels).reduce((acc: ColumnModelItem[], curr: string) => {
    if (curr !== "_id" && curr !== "source") { // Don't need "_id" or "source" fields in columns UI
      acc.push({
        id: curr,
        field: curr,
        headerName: fieldLabels[curr],
        hidden: false,
        fieldType: fieldTypes[curr],
        sourceField: curr
      });
    }

    return acc;
  }, []);

  const widgetModelColumns: ColumnModelItem[] = (props?.columnModel?.columns && props?.columnModel?.columns.length) ?
    props?.columnModel?.columns :
    initialModelColumns;

  const dataGridColumns: GridColDef[] = widgetModelColumns
    // For widgets that already have the "adult_content" filter column added
    .filter((col: ColumnModelItem) => col.field !== "adult_content")
    .map((column: ColumnModelItem, index: number) => {
      const headerName = column.headerName === fieldLabels[column.field] ? fieldLabels[column.field] : column.headerName;
      const labelOptions = Object.keys(props.apiResponse.value_labels);

      return {
        ...column,
        field: column.field,
        headerName,
        minWidth: columnWidth,
        hideable: true,
        headerAlign: 'center',
        sortable: false,
        key: index,
        ...getColumnType(column.fieldType),
        ...(column.field === 'body' && {
            valueFormatter: value => <SanitizedHTML html={value} forbidTable={true} />
          }
        ),
        // timestamp fields need to be a Date object
        ...(column.fieldType === 'timestamp' &&
          {
            valueGetter: (value: any) => new Date(value),
            renderCell: params => <ISODateTime isoString={params.value.toString()} />
          }
        ),
        ...(column.fieldType === 'integer' &&
          {
            renderCell: (params: any) => {
              if (params.value) {
                return <ISONumber number={params.value} />;
              } else {
                return undefined;
              }
            }
          }
        ),
        ...(labelOptions.includes(column.field) && {
          renderCell: (params: any) => {
            // params.value is either an array of values, a single string, or undefined
            // emotions: ['love', 'joy', 'anticipation'] OR language: es
            // If value doesn't exit on row it won't be undefined. It just won't exist.
            
            if (params.value) {
              const fieldValueLabels = getValueLabels({
                apiValueLabels: props.apiResponse.value_labels,
                field: column.field,
                lookupKey: params.value
              });

              if (Array.isArray(fieldValueLabels)) {
                return (
                  <Box>
                    {fieldValueLabels.map((item: string, idx: number, arr: string[]) => {
                      return (
                        <Typography key={item}>
                          <span>{item}</span>
                          {idx !== arr.length - 1 && <span>,</span>}
                        </Typography>
                      );
                    })}
                  </Box>
                );
              } else {
                return <Chip label={fieldValueLabels} size="small" />
              }
            }
          }
        })
      };
  });

  const modelToMUIInitialState = () => {
    // If columns from the props model is available, use it, otherwise use the initial columns
    const columnFields = widgetModelColumns;
    const orderedFields = columnFields.map((item: ColumnModelItem) => item.field);
    let columnVisibilityModel = widgetModelColumns.reduce((acc, curr) => {
      if (curr.hidden === true) {
        return {
          ...acc,
          [curr.field]: false,
        };
      } else {
        return acc;
      }
    }, {}) ?? {};

    const gridStateObject: GridInitialStateCommunity = {
      columns: {
        orderedFields: orderedFields,
        columnVisibilityModel
      },
      sorting: {
        sortModel: props?.columnModel?.sorting?.sortModel ?? []
      }
    };

    return gridStateObject;
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {records.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={props?.currentState?.widgetTitleAlignment}
              customTitle={props?.currentState?.customWidgetTitle}
              defaultTitle={i18n.t("widget-types.record-table.label")}
              settingsTitle={props?.currentState?.widgetTitle ?? ""}
              style={{ pb: 2 }}
              widgetQueryTitle={props.widgetQueryTitle} />
          }
          footer={pageCount > 1 &&
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", borderTop: `1px solid ${theme.palette.divider}`, p: 1 }}>
              <Pagination
                count={pageCount}
                onChange={handlePageChange}
                page={pageNumber}
                shape="rounded"
              />
              {props.onFetchMore && 
                <>
                  {isAtEnd && 
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={handleFetchMore}
                    >
                      {i18n.t("widgets.settings.pagination.load-more")}
                    </Button>
                  }
                </>
              }
            </Box>
          }
        >
          <StripedDataGrid
            getRowHeight={() => "auto"}
            showRowStripes={props.currentState?.rowStripes}
            ref={tableRef}
            columns={dataGridColumns}
            rows={paginatedRows}
            initialState={modelToMUIInitialState()}
            loading={rows.length === 0}
            density={props?.currentState?.density ?? "standard"}
            disableColumnFilter
            disableColumnMenu
            hideFooter
          />
        </WidgetLayout>
      }
    </Box>
  );
}
